import React, { useState, useContext } from 'react'
import ReactDOM from 'react-dom'
import { useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import useLearnerContext from '../hooks/useLearnerContext.js'
import languages, { getLanguageAcronym } from '../languages.js'
import { ADD_MEETING_WITH_CONTENT_MUTATION } from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'
import { streamOllamaGenerate } from '../ollama.js'

// Text models only — this is pure generation (no OCR). gemma4:26b, the Mac
// Studio's model for everything, writes grammar-accurate lessons quickly;
// gemma3:27b stays while it's installed.
const MODELS = ['gemma4:26b', 'gemma3:27b']

function parseLooseJson(raw) {
  let s = String(raw || '').replace(/```json/gi, '').replace(/```/g, '').trim()
  try { return JSON.parse(s) } catch { /* salvage */ }
  const a = s.indexOf('{'); const b = s.lastIndexOf('}')
  if (a >= 0 && b > a) { try { return JSON.parse(s.slice(a, b + 1)) } catch { /* give up */ } }
  return null
}

// Same normalized key the meeting importer uses, so trivial variants collapse.
const dedupKey = (s) => String(s || '').normalize('NFC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
const dedupe = (arr, keyOf) => Array.from(new Map(arr.filter(i => dedupKey(keyOf(i))).map(i => [dedupKey(keyOf(i)), i])).values())

export default function AddLessonFromQueryDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const { personalize } = useLearnerContext(username)

  const [query, setQuery] = useState('')
  const [lessonTitle, setLessonTitle] = useState('')
  const [targetLanguage, setTargetLanguage] = useState('Russian')
  const [model, setModel] = useState('gemma4:26b')
  const [lessonDate, setLessonDate] = useState(new Date().toISOString().split('T')[0])
  const [status, setStatus] = useState('idle') // idle | generating | reviewing | saving | completed | error
  const [errorMessage, setErrorMessage] = useState('')
  const [progressMsg, setProgressMsg] = useState('')
  const [previewData, setPreviewData] = useState(null)

  const [addMeetingWithContent] = useMutation(ADD_MEETING_WITH_CONTENT_MUTATION)

  const isOpen = dialogStates['dialogAddLessonFromQuery']

  const handleClose = () => {
    closeDialog('dialogAddLessonFromQuery')
    setQuery(''); setLessonTitle(''); setStatus('idle'); setErrorMessage('')
    setProgressMsg(''); setPreviewData(null)
    setLessonDate(new Date().toISOString().split('T')[0])
  }

  const handleGenerate = async () => {
    if (!query.trim()) return
    setStatus('generating')
    setErrorMessage('')
    setProgressMsg('Designing the lesson…')

    const srcLang = getLanguageAcronym(targetLanguage) || 'eng'

    const personal = personalize(targetLanguage)
    const prompt = `You are an expert ${targetLanguage} teacher. Build a focused, practical practice lesson that TEACHES and DRILLS the topic/notes below, for a ${personal ? 'learner' : 'English speaker'} learning ${targetLanguage}.
${personal ? `\n${personal}\n` : ''}
TOPIC / NOTES:
${query.trim()}

Create:
- "sentences": 10-16 natural example sentences IN ${targetLanguage} that clearly demonstrate the topic across its different points (mostly beginner→intermediate), each with an English translation. Prefer sentences that make the grammar point obvious and vary the vocabulary.
- "words": 10-20 key words or short phrases IN ${targetLanguage} worth memorising for this topic (question words, key verbs, markers/endings, etc.), each with an English translation.
- "tasks": 2-3 short homework tasks to practise it (title, type "Homework", description referencing the words/sentences, priority "Low"/"Medium"/"High").

RULES:
- Every "sourceText" and "word" MUST be written in ${targetLanguage}, never English.
- Never leave a translation blank.
- Return ONLY a valid JSON object, no markdown, no commentary.

{
  "sentences": [ { "sourceText": "", "targetText": "" } ],
  "words": [ { "word": "", "translation": "" } ],
  "tasks": [ { "title": "", "type": "Homework", "description": "", "priority": "Medium" } ]
}`

    try {
      const raw = await streamOllamaGenerate({
        body: { model, prompt, format: 'json', options: { temperature: 0.5, num_predict: 2600 } },
        onProgress: (acc) => setProgressMsg(`Writing the lesson… (${acc.length} chars)`),
      })
      const parsed = parseLooseJson(raw)
      if (!parsed) { setStatus('error'); setErrorMessage('The model returned something unparseable — try again or a different model.'); return }

      const sentences = dedupe(
        (parsed.sentences || [])
          .map(s => ({ sourceText: String(s.sourceText || '').trim(), targetText: String(s.targetText || '').trim(), sourceLang: srcLang, targetLang: 'eng' }))
          .filter(s => s.sourceText),
        s => s.sourceText
      )
      const words = dedupe(
        (parsed.words || [])
          .map(w => ({ word: String(w.word || '').trim(), translation: String(w.translation || '').trim(), lang: srcLang }))
          .filter(w => w.word),
        w => w.word
      )
      const tasks = (parsed.tasks || [])
        .map(t => ({ title: String(t.title || '').trim(), type: t.type || 'Homework', description: String(t.description || '').trim(), priority: t.priority || 'Medium' }))
        .filter(t => t.title)
        .slice(0, 3)

      if (sentences.length === 0 && words.length === 0) {
        setStatus('error'); setErrorMessage('No lesson content came back — try rephrasing the topic or a different model.'); return
      }

      setPreviewData({ sentences, words, tasks })
      setStatus('reviewing')
    } catch (err) {
      console.error(err)
      setStatus('error')
      setErrorMessage(
        (err.message || '').match(/Failed to fetch|Load failed/)
          ? 'Connection to the Mac Studio failed. Check the tunnel / that Ollama is reachable, or try a smaller model.'
          : (err.message || 'Generation failed.')
      )
    }
  }

  const handleSave = async () => {
    if (!previewData) return
    setStatus('saving')
    try {
      await addMeetingWithContent({
        variables: {
          input: {
            meeting: {
              title: lessonTitle || 'AI Lesson',
              type: 'Language Class',
              description: `AI lesson: ${query.trim().slice(0, 140)}`,
              meetingDate: lessonDate || null,
              username,
            },
            sentences: previewData.sentences || [],
            words: previewData.words || [],
            tasks: previewData.tasks || [],
          },
        },
      })
      setStatus('completed')
      setTimeout(handleClose, 1800)
    } catch (err) {
      console.error(err)
      setStatus('error')
      setErrorMessage(err.message)
    }
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <button className={styles.closeBtn} onClick={handleClose}><CloseIcon /></button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>AI Lesson</div>
            <h2 className={styles.title}>Build a lesson from a topic</h2>
          </div>
        </div>

        <div className={styles.container}>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Lesson Title</div>
              <input type="text" className={styles.input} value={lessonTitle} onChange={(e) => setLessonTitle(e.target.value)} placeholder="e.g. Russian Cases" />
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Date</div>
              <input type="date" className={styles.input} value={lessonDate} onChange={(e) => setLessonDate(e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Target Language</div>
              <select className={styles.input} value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)}>
                {Object.entries(languages).map(([key, value]) => (
                  <option key={key} value={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <div className={styles.fieldLabel}>Ollama Model</div>
              <select className={styles.input} value={model} onChange={(e) => setModel(e.target.value)}>
                {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Topic / notes to teach & practise</div>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={'Paste a grammar point, a set of notes, or just a topic.\ne.g. "The 6 Russian cases — what each is for and the question words", or paste your class notes.'}
              spellCheck="false"
              style={{
                width: '100%', minHeight: '180px', resize: 'vertical',
                background: '#0d0d0f', border: '1px solid #3a3a42', borderRadius: '8px',
                color: '#e8e8ec', fontFamily: "'DM Mono', monospace", fontSize: '0.85rem',
                lineHeight: 1.5, padding: '0.7rem', boxSizing: 'border-box', whiteSpace: 'pre-wrap',
              }}
            />
          </div>

          {status === 'generating' && (
            <div style={{ color: '#6ee7b7', fontSize: '0.9rem', marginTop: '0.5rem', fontStyle: 'italic', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <div>Asking the Mac Studio to write the lesson…</div>
              {progressMsg && <div style={{ fontWeight: 'bold' }}>{progressMsg}</div>}
            </div>
          )}
          {status === 'error' && <div style={{ color: '#ff4d4d', fontSize: '0.9rem', marginTop: '0.5rem' }}>{errorMessage}</div>}
          {status === 'completed' && <div style={{ color: '#6ee7b7', fontSize: '0.9rem', marginTop: '0.5rem' }}>Saved! Find it in Past Lessons and practise it with Flow / Recall / Verb Drill.</div>}

          {status === 'reviewing' && previewData && (
            <div style={{ marginTop: '0.5rem', padding: '1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', maxHeight: '300px', overflowY: 'auto', border: '1px solid #3a3a42' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#6ee7b7' }}>Preview lesson</h4>

              <div style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>Sentences ({previewData.sentences?.length || 0})</div>
              {previewData.sentences?.map((s, i) => (
                <div key={i} style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                  <span style={{ color: '#6ee7b7' }}>{s.sourceText}</span> → {s.targetText}
                </div>
              ))}

              <div style={{ marginBottom: '0.5rem', marginTop: '1rem', fontWeight: 'bold' }}>Words ({previewData.words?.length || 0})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {previewData.words?.map((w, i) => (
                  <span key={i} style={{ fontSize: '0.8rem', background: '#222226', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>{w.word} = {w.translation}</span>
                ))}
              </div>

              <div style={{ marginBottom: '0.5rem', marginTop: '1rem', fontWeight: 'bold' }}>Tasks ({previewData.tasks?.length || 0})</div>
              {previewData.tasks?.map((t, i) => (
                <div key={i} style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                  <strong style={{ color: '#6ee7b7' }}>{t.title}</strong> ({t.type}): {t.description}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {status !== 'reviewing' && status !== 'saving' && status !== 'completed' && (
            <button className={styles.primaryBtn} onClick={handleGenerate} disabled={!query.trim() || status === 'generating'}>
              {status === 'generating' ? 'Generating…' : 'Generate Lesson'}
            </button>
          )}
          {status === 'reviewing' && (
            <>
              <button className={styles.primaryBtn} onClick={handleGenerate} style={{ background: 'transparent', border: '1px solid #3a3a42' }}>Regenerate</button>
              <button className={styles.primaryBtn} onClick={handleSave}>Save Lesson</button>
            </>
          )}
          {status === 'saving' && <button className={styles.primaryBtn} disabled>Saving…</button>}
        </div>
      </div>
    </div>,
    document.body
  )
}
